import { ed25519 } from "@noble/curves/ed25519";
import { fromHex, toHex } from "./account.js";

/**
 * Reçu de session (dossier d'architecture, ch. 7.4, arbitrage PO-02).
 *
 * À la clôture, le serveur signe un reçu et l'envoie au participant, puis
 * l'oublie. L'appareil le conserve. N'importe qui, plus tard, peut vérifier
 * le reçu avec la seule clé publique du serveur : aucune base de données
 * n'est nécessaire, et un reçu ne peut pas être fabriqué par un participant.
 */

export interface ReceiptBody {
  v: 1;
  /** Clé publique du participant, hexadécimal. */
  pub: string;
  session: string;
  sector: string;
  theme: string | null;
  outcome: "met" | "almost" | "no" | "left";
  /** Horodatage serveur, en secondes. */
  ts: number;
}

export interface Receipt extends ReceiptBody {
  /** Signature Ed25519 du corps canonique, hexadécimal. */
  sig: string;
  /** Clé publique du serveur ayant signé, pour retrouver la bonne clé. */
  signer: string;
}

/**
 * Encodage canonique : clés dans un ordre fixe, aucun espace. Deux
 * implémentations doivent produire exactement les mêmes octets.
 */
export function canonicalize(body: ReceiptBody): string {
  return JSON.stringify([body.v, body.pub, body.session, body.sector, body.theme, body.outcome, body.ts]);
}

export function signReceipt(body: ReceiptBody, serverPrivateKeyHex: string): Receipt {
  const sk = fromHex(serverPrivateKeyHex);
  const msg = new TextEncoder().encode(canonicalize(body));
  return {
    ...body,
    sig: toHex(ed25519.sign(msg, sk)),
    signer: toHex(ed25519.getPublicKey(sk)),
  };
}

export function verifyReceipt(receipt: Receipt, expectedSignerHex?: string): boolean {
  try {
    if (expectedSignerHex && receipt.signer !== expectedSignerHex) return false;
    const { sig, signer, ...body } = receipt;
    const msg = new TextEncoder().encode(canonicalize(body as ReceiptBody));
    return ed25519.verify(fromHex(sig), msg, fromHex(signer));
  } catch {
    return false;
  }
}

/**
 * Réputation vérifiable (ch. 7.5) : une somme de reçus valides, un seul par
 * session et par clé. Aucune note attribuée par un autre humain.
 */
export function summarizeReceipts(
  receipts: readonly Receipt[],
  opts: { pub: string; trustedSigners?: readonly string[] },
): { sessions: number; met: number; almost: number; invalid: number } {
  const seen = new Set<string>();
  let sessions = 0;
  let met = 0;
  let almost = 0;
  let invalid = 0;
  for (const r of receipts) {
    const trusted = !opts.trustedSigners || opts.trustedSigners.includes(r.signer);
    if (r.pub !== opts.pub || !trusted || !verifyReceipt(r) || seen.has(r.session)) {
      invalid++;
      continue;
    }
    seen.add(r.session);
    sessions++;
    if (r.outcome === "met") met++;
    if (r.outcome === "almost") almost++;
  }
  return { sessions, met, almost, invalid };
}
